import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";

const StripeCard = () => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.stripeConnect().then(setSt).catch(() => setSt({ available: false })); }, []);
  const start = async () => {
    setBusy(true);
    try { window.location.href = (await api.stripeConnectStart()).url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't reach Stripe"); setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Stop taking card payments? Clients will only see your bank details.")) return;
    setSt({ ...st, ...(await api.stripeDisconnect()) });
  };
  if (!st) return <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />;
  if (!st.available) return <p className="text-xs text-muted-foreground" data-testid="stripe-unavailable">Card payments can't be connected on the demo company.</p>;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border p-3" data-testid="stripe-connect-card">
      <div className="flex items-start gap-2.5 text-sm">
        {st.ready ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" /> : <CreditCard className="w-5 h-5 text-muted-foreground shrink-0" />}
        <div>
          <p className="font-medium" data-testid="stripe-status">{st.ready ? "Stripe connected: clients can pay by card" : st.connected ? "Stripe setup not finished" : "Take card payments with Stripe"}</p>
          <p className="text-xs text-muted-foreground">{st.ready ? "Payments go straight to your own Stripe account." : "Link your own Stripe account. Stripe's card fees apply; Cross Jobs takes nothing."}</p>
        </div>
      </div>
      {st.can_manage ? (
        st.ready ? <Button variant="ghost" size="sm" onClick={disconnect} className="rounded-full text-destructive" data-testid="stripe-disconnect-btn">Disconnect</Button>
          : <Button size="sm" disabled={busy} onClick={start} className="rounded-full bg-[#635bff] hover:bg-[#5146e5] text-white" data-testid="stripe-connect-btn">{busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}{st.connected ? "Finish setup" : "Connect Stripe"}</Button>
      ) : <p className="text-xs text-muted-foreground">Ask your company login to connect Stripe.</p>}
    </div>
  );
};

export const PaymentFields = ({ f, set }) => (
  <section className="space-y-4" data-testid="settings-payment">
    <h3 className="font-semibold">Getting paid</h3>
    <div className="grid sm:grid-cols-3 gap-3">
      <div className="space-y-1.5 sm:col-span-3 max-w-[200px]"><Label className="text-xs">Payment terms (days)</Label>
        <Input value={f.terms_days} inputMode="numeric" onChange={(e) => set("terms_days", e.target.value)} data-testid="settings-terms-days" /></div>
      <div className="space-y-1.5"><Label className="text-xs">Bank account name</Label><Input value={f.bank_name} onChange={(e) => set("bank_name", e.target.value)} data-testid="settings-bank-name" /></div>
      <div className="space-y-1.5"><Label className="text-xs">BSB</Label><Input value={f.bank_bsb} inputMode="numeric" placeholder="000-000" onChange={(e) => set("bank_bsb", e.target.value)} data-testid="settings-bank-bsb" /></div>
      <div className="space-y-1.5"><Label className="text-xs">Account number</Label><Input value={f.bank_account} inputMode="numeric" onChange={(e) => set("bank_account", e.target.value)} data-testid="settings-bank-account" /></div>
    </div>
    <label className="flex items-center justify-between gap-3 text-sm"><span>Show a "Pay by card" button on invoices</span>
      <Switch checked={f.card} onCheckedChange={(v) => set("card", v)} data-testid="settings-card-toggle" /></label>
    {f.card && <StripeCard />}
  </section>
);
